import React, { useCallback, useEffect, useRef } from 'react';
import { Button } from '../ds/components/Button';
import { Icon, IconName } from '../ds/components/Icon';
import { ProgressBar } from '../ds/components/ProgressBar';
import { useCeebPhaseAPolling } from '../hooks/useCeebPhaseAPolling';
import { PhaseAStep } from '../services/api';

interface Props {
  buildingId: string;
  /** Numer sprawy zapisany u nas — gdy jest, Faza A tego cyklu jest już domknięta. */
  caseNumber?: string | null;
  /** Po świeżym `done` rodzic przeładowuje budynek, żeby zaciągnąć numer sprawy i prefill. */
  onFinished?: () => void;
}

type Tone = 'info' | 'success' | 'warn' | 'error';

// Tony na tokenach DS (bez hardkodów kolorów) — ten sam zestaw co w banerze rejestracji,
// żeby oba stany CEEB wyglądały spójnie.
const TONE: Record<Tone, { bg: string; bar: string; color: string; icon: IconName }> = {
  info: { bg: 'var(--changed-bg)', bar: 'var(--action-primary)', color: 'var(--changed-text)', icon: 'refresh' },
  success: { bg: 'var(--status-ok-bg)', bar: 'var(--status-ok-bar)', color: 'var(--status-ok)', icon: 'check-circle' },
  warn: { bg: 'var(--status-rec-bg)', bar: 'var(--status-rec-bar)', color: 'var(--status-rec)', icon: 'triangle-alert' },
  error: { bg: 'var(--status-deny-bg)', bar: 'var(--status-deny-bar)', color: 'var(--status-deny)', icon: 'triangle-alert' },
};

const IKONA_KROKU: Record<PhaseAStep['state'], { znak: string; kolor: string }> = {
  done: { znak: '✓', kolor: 'var(--status-ok)' },
  running: { znak: '…', kolor: 'var(--action-primary)' },
  error: { znak: '✕', kolor: 'var(--status-deny)' },
  skipped: { znak: '–', kolor: 'var(--text-secondary)' },
  pending: { znak: '·', kolor: 'var(--text-secondary)' },
};

/**
 * Zadanie 5.6 (2026-08-20): pasek postępu FAZY A na karcie budynku.
 *
 * Faza A rusza automatycznie przy dodaniu budynku (AddBuildingModal) i biegnie w tle, więc ten
 * panel niczego nie startuje sam — POKAZUJE, co się dzieje, i pozwala PONOWIĆ po błędzie.
 * To zastępuje przycisk „Zarejestruj w CEEB" (Zadanie 5.1): rejestracja przestała być osobną
 * czynnością operatora i jest krokiem 1–2 tego zadania.
 *
 * Gdy Faza A padnie, budynek ZOSTAJE u nas — z informacją, na którym kroku stanęło i przyciskiem
 * „Ponów". Nie chowamy błędu w konsoli (KONWENCJE: błąd musi być widoczny dla użytkownika).
 */
const CeebPhaseAPanel: React.FC<Props> = ({ buildingId, caseNumber, onFinished }) => {
  const { status, result, start, observe } = useCeebPhaseAPolling();
  const awaitingRef = useRef(false);

  // Wejście na kartę budynku: dołącz do trwającego przebiegu (albo pokaż jego wynik).
  useEffect(() => {
    void observe(buildingId);
  }, [buildingId, observe]);

  useEffect(() => {
    if (!awaitingRef.current) return;
    if (status !== 'done' && status !== 'error') return;
    awaitingRef.current = false;
    if (status === 'done') onFinished?.();
    // onFinished stabilne w obrębie montażu rodzica; celowo poza deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleRetry = useCallback(() => {
    if (status === 'running') return;
    awaitingRef.current = true;
    void start(buildingId);
  }, [status, start, buildingId]);

  // Nic się nie dzieje i nie ma czego pokazać — panel milczy.
  if (status === 'idle' && !result) return null;

  const kroki = result?.steps || [];
  const stepCount = result?.stepCount || kroki.length || 5;
  const zrobione = kroki.filter((k) => k.state === 'done' || k.state === 'skipped').length;

  let tone: Tone = 'info';
  let text = '';
  if (status === 'running') {
    tone = 'info';
    text = result?.step
      ? `Przygotowanie budynku w CEEB — krok ${result.stepIndex ?? '?'} z ${stepCount}: ${result.step}.`
      : 'Przygotowanie budynku w CEEB… Nie zamykaj tej strony.';
  } else if (status === 'done' && result?.code === 'SECOND_PROTOCOL_IN_YEAR') {
    // Drugi protokół w roku (2026-10-10, D6): Faza A zapisała numer obok protokołu wystawionego w tym roku (albo
    // go jeszcze nie założyła) — wysyłka zapisze go tylko jako wersję roboczą, wystawia człowiek.
    tone = 'warn';
    const nr = result?.caseNumber || caseNumber;
    const juz = (result?.decision?.issuedThisYear || [])
      .map((p) => `${p.caseNumber}${p.status ? ` („${p.status}”)` : ''}`).join(', ');
    text = `${nr ? `Budynek przygotowany w CEEB — sprawa ${nr}. ` : ''}Uwaga: w CEEB jest już protokół z tego roku`
      + `${juz ? ` (${juz})` : ''} — „Wyślij do CEEB” ${nr ? 'zapisze ten protokół' : 'założy drugi protokół i zapisze go'} `
      + 'tylko jako wersję roboczą. Jeśli to drugi termin / zaległa kontrola — sprawdź i sfinalizuj „Finalizuj w CEEB”; '
      + 'jeśli to duplikat — nie wystawiaj; zgłoś administratorowi.';
  } else if (status === 'done') {
    tone = 'success';
    const nr = result?.caseNumber || caseNumber;
    text = nr
      ? `Budynek przygotowany w CEEB — sprawa ${nr}. Dane z poprzedniej kontroli zostały zaciągnięte.`
      : 'Budynek przygotowany w CEEB.';
  } else if (status === 'error') {
    // Rozróżniamy „protokół tego cyklu jest zamknięty" (nie ponawiamy — to nie awaria)
    // od zwykłej awarii, którą warto powtórzyć.
    const zablokowany = result?.decision?.action === 'blocked' || /zamknięt|akceptacj|Wprowadzon/i.test(result?.error || '');
    tone = zablokowany ? 'warn' : 'error';
    text = result?.error || 'Nie udało się przygotować budynku w CEEB.';
  }

  const meta = TONE[tone];
  const pokazPonow = status === 'error' && tone === 'error';

  return (
    <div
      data-testid="phase-a-panel"
      data-status={status}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}
    >
      <div
        role={status === 'error' ? 'alert' : 'status'}
        aria-live="polite"
        data-tone={tone}
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          padding: 'var(--space-3) var(--space-4)',
          background: meta.bg,
          borderLeft: `3px solid ${meta.bar}`,
          borderRadius: 'var(--radius-sm)',
          color: meta.color,
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-body)',
        }}
      >
        <Icon name={meta.icon} size={18} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', flex: 1 }}>
          <span>{text}</span>

          {kroki.length > 0 && (
            <>
              <ProgressBar
                value={zrobione}
                max={stepCount}
                showCount={false}
                label={`${zrobione} z ${stepCount} kroków`}
              />
              <ol style={{ margin: 0, paddingLeft: 'var(--space-4)', listStyle: 'none' }}>
                {kroki.map((k) => (
                  <li
                    key={k.name}
                    data-step={k.name}
                    data-step-state={k.state}
                    style={{
                      display: 'flex',
                      gap: 'var(--space-2)',
                      fontSize: 'var(--text-label)',
                      color: k.state === 'pending' ? 'var(--text-secondary)' : 'inherit',
                    }}
                  >
                    <span aria-hidden style={{ color: IKONA_KROKU[k.state].kolor, width: '1em' }}>
                      {IKONA_KROKU[k.state].znak}
                    </span>
                    <span>
                      {k.name}
                      {k.info ? <span style={{ color: 'var(--text-secondary)' }}> — {k.info}</span> : null}
                    </span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      </div>

      {pokazPonow && (
        <div>
          <Button variant="secondary" icon="refresh" onClick={handleRetry}>
            Ponów przygotowanie w CEEB
          </Button>
        </div>
      )}
    </div>
  );
};

export default CeebPhaseAPanel;
